import {
    addMutableWidgets,
    makeSelectMutableWidget,
    recordWidgetRender,
    selectCurrentPageId,
    selectMutableWidgetsMap,
    updateWidgetDataByKey,
    updateWidgetValue,
    updateWidgetValueByKey,
    useAppDispatch,
    useAppSelector,
    websoketService,
} from '../store';
import {
    ActionActionTypeEnum,
    TCommonData,
    TCommonWidgetValue,
    TItem,
    TListData,
    TListMethodsRef,
    TPayloadWidget,
    TUpdateWidgetValueViaActionMethodsRef,
    TUseGetWidget,
    TUseListHook,
    TUseUpdateWidget,
    TUseUpdateWidgetValue,
    TUseUpdateWidgetValueViaAction,
    TWAction,
    TWidget,
} from '@libs/types';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useGetWidget = <TD, TO>(wid: TWidget<TD, TO>['uid'], widget?: TWidget<TD, TO>): TUseGetWidget<TD, TO> => {
    const selectWidget = useMemo(() => makeSelectMutableWidget(), []);
    const widgetFromRedux = useAppSelector((state) => selectWidget(state, wid));
    recordWidgetRender(wid);
    return {
        widget: (widgetFromRedux as TWidget<TD, TO>) ?? widget,
    };
};

export const useUpdateWidgetValue = <TD extends TCommonData>(widget: TWidget<TD>): TUseUpdateWidgetValue => {
    const dispatch = useAppDispatch();

    return {
        updateWidgetValue: useCallback(
            (value: TCommonWidgetValue) =>
                dispatch(
                    updateWidgetValue({
                        value,
                        uid: widget.uid,
                    }),
                ),
            [dispatch, widget.uid],
        ),
        updateWidgetValueByKey: useCallback(
            (value: TCommonWidgetValue, key: string) =>
                dispatch(
                    updateWidgetValueByKey({
                        value,
                        key,
                        uid: widget.uid,
                    }),
                ),
            [dispatch, widget.uid],
        ),
    };
};

export const useUpdateWidgetValueViaAction = (methodsRef: TUpdateWidgetValueViaActionMethodsRef): TUseUpdateWidgetValueViaAction => {
    const { triggerActionsByNameAndType } = methodsRef;

    return {
        updateWidgetValueViaAction: useCallback(
            (actionName, actionType, value) => {
                triggerActionsByNameAndType(actionType, actionName, { value }).then();
            },
            [triggerActionsByNameAndType],
        ),
    };
};

export const useUpdateWidget = <TD extends TCommonData, TO>(wid: string): TUseUpdateWidget<TD, TO> => {
    const dispatch = useAppDispatch();
    const selectWidget = useMemo(() => makeSelectMutableWidget(), []);
    const mutableWidget = useAppSelector((state) => selectWidget(state, wid)) as TWidget<TD, TO>;
    return {
        mutableWidget,
        updateWidget: useCallback(
            (widget: TWidget<TD, TO>) => {
                dispatch(
                    addMutableWidgets({
                        widgets: widget as unknown as TPayloadWidget<TCommonData>,
                        widgetUid: wid,
                    }),
                );
            },
            [dispatch, wid],
        ),
        addMutableWidgets: (widgets: Array<TWidget<TD>>) => {
            dispatch(
                addMutableWidgets({
                    widgets: widgets as unknown as Array<TPayloadWidget<TCommonData>>,
                    widgetUid: wid,
                }),
            );
        },
        updateWidgetDataByKey: useCallback(
            (key: string, value: TCommonWidgetValue) => {
                dispatch(
                    updateWidgetDataByKey({
                        value,
                        key,
                        uid: wid,
                    }),
                );
            },
            [dispatch, wid],
        ),
    };
};

export const useSubscribeOnWsUpdate = (action?: TWAction) => {
    const isSubscribed = useRef(false);
    const currentPageId = useAppSelector(selectCurrentPageId);
    const mutableWidgetsMap = useAppSelector(selectMutableWidgetsMap);
    const mutableWidgetsMapRef = useRef(mutableWidgetsMap);

    useEffect(() => {
        if (action && !isSubscribed.current) {
            websoketService.subscribeWidget(action, currentPageId, mutableWidgetsMapRef.current);
            isSubscribed.current = true;
        }
    }, [action, currentPageId]);
};

export const useList = (widget: TWidget<TListData>, methodsRef: TListMethodsRef, triggerRef?: RefObject<HTMLInputElement | null>): TUseListHook => {
    const dispatch = useAppDispatch();
    const { triggerActionsByType, setModal, setPopup, setBottomSheet } = methodsRef;
    const [hasReachedEnd, setHasReachedEnd] = useState(false);
    const observer = useRef<IntersectionObserver>(null);
    const actions = useMemo(() => widget?.actions ?? [], [widget?.actions]);

    useEffect(() => {
        if (observer.current) {
            observer.current.disconnect();
        }
        const callback = (entries: IntersectionObserverEntry[]) => {
            setHasReachedEnd(entries[entries.length - 1].isIntersecting);
        };
        observer.current = new IntersectionObserver(callback);
        if (triggerRef?.current) {
            observer.current.observe(triggerRef.current);
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [triggerRef]);

    const handleClick = useCallback(
        (uid: string, item: TItem) => {
            const value = item?.value;
            dispatch(updateWidgetValue({ value: value, uid }));
            triggerActionsByType(ActionActionTypeEnum.onItemSelected, { value }).then();
            if (actions.find((a) => a.actionType === ActionActionTypeEnum.onItemSelected)) {
                setModal?.(null);
                setPopup?.(null);
                setBottomSheet?.(undefined);
            }
        },
        [dispatch, triggerActionsByType, actions, setModal, setPopup, setBottomSheet],
    );

    return {
        handleClick,
        hasReachedEnd,
    };
};
